import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import {
  BarChart3, FileText, BookOpen, Scale, TrendingUp, TrendingDown,
  ClipboardList, Building2, ShoppingCart, Receipt, Truck, Wallet,
  Clock, CreditCard, Banknote, PieChart, ArrowLeftRight, GitCompare,
  Repeat, CalendarDays, DollarSign, Layers, Activity, UserCheck,
  Award, BarChart2, Factory, Wrench, TestTubes, Boxes, Brain,
  Zap, Globe, Target, AlertTriangle, Download, Eye, Star, Search, X
} from 'lucide-react';
import { ZatcaCsidBanner } from '@/components/banking/ZatcaCsidBanner';

const reportGroups = [
  {
    title: 'Core Accounting',
    color: 'from-indigo-500 to-violet-600',
    reports: [
      { path: '/trial-balance', icon: Scale, label: 'Trial Balance', desc: 'Debit & credit balances for all accounts' },
      { path: '/general-ledger', icon: BookOpen, label: 'General Ledger', desc: 'Complete ledger with all transactions' },
      { path: '/journal-entries', icon: FileText, label: 'Journal Entries', desc: 'Manual debit and credit entries' },
      { path: '/balance-sheet', icon: Layers, label: 'Balance Sheet', desc: 'Assets, liabilities and equity snapshot' },
      { path: '/profit-loss', icon: BarChart3, label: 'Income Statement (P&L)', desc: 'Revenue minus expenses for a period' },
      { path: '/branch-summary', icon: GitCompare, label: 'Branch Summary', desc: 'Sales by payment, COGS & expenses per branch' },
      { path: '/pnl-compare', icon: GitCompare, label: 'P&L Compare', desc: 'Side-by-side period comparison' },
    ],
  },
  {
    title: 'Account Reports',
    color: 'from-teal-500 to-cyan-600',
    reports: [
      { path: '/account-statement', icon: FileText, label: 'Account Statement', desc: 'Transaction history for a specific account' },
      { path: '/account-summary', icon: ClipboardList, label: 'Account Summary', desc: 'All accounts with debit/credit totals' },
      { path: '/chart-of-accounts', icon: Layers, label: 'Chart of Accounts', desc: 'Define your account structure' },
    ],
  },
  {
    title: 'Tax & VAT',
    color: 'from-emerald-500 to-green-600',
    reports: [
      { path: '/sales-vat-report', icon: TrendingUp, label: 'Sales VAT Report', desc: 'VAT collected on sales' },
      { path: '/purchases-vat-report', icon: TrendingDown, label: 'Purchases VAT Report', desc: 'VAT paid on purchases & bills' },
      { path: '/vat-return', icon: Scale, label: 'VAT Return', desc: 'Multi-country: ZATCA/FTA/NBR/HMRC/GSTR formats' },
      { path: '/wht', icon: Receipt, label: 'Withholding Tax (WHT)', desc: 'KSA, UAE, BH, OM, QA non-resident WHT register + certificates' },
      { path: '/tax-submissions', icon: Receipt, label: 'Tax Submissions', desc: 'Generate ZATCA / FTA / NBR / HMRC / GSTR-3B payloads' },
      { path: '/zatca-phase2', icon: Receipt, label: 'ZATCA Phase 2', desc: 'Optional e-invoicing CSID setup (KSA)' },
      { path: '/hmrc-mtd', icon: Receipt, label: 'HMRC MTD (UK)', desc: 'Live OAuth + VAT submission to HMRC Making Tax Digital' },
      { path: '/manager/inbox', icon: BarChart2, label: 'Manager Inbox', desc: 'Unified queue: pending claims · leaves · disputes · overdue compliance' },
      { path: '/fx-rates', icon: Receipt, label: 'FX Rates', desc: 'Multi-currency exchange rates + auto-fetch' },
      { path: '/compliance', icon: Receipt, label: 'Compliance Reminders', desc: 'CR / VAT / licenses expiry tracking + alerts' },
      { path: '/tax-settings', icon: Receipt, label: 'Tax & Currency', desc: 'Multi-country tax setup, branch overrides, audit log' },
    ],
  },
  {
    title: 'Sales & Revenue Reports',
    color: 'from-amber-500 to-orange-600',
    reports: [
      { path: '/sales-summary', icon: ShoppingCart, label: 'Sales Summary', desc: 'Unified roll-up: daily, branch, customer, payment mode' },
      { path: '/analytics', icon: BarChart3, label: 'Sales Analytics', desc: 'Charts and trends for sales data' },
      { path: '/category-report', icon: BarChart3, label: 'Category Report', desc: 'Supplier category breakdown' },
      { path: '/credit-report', icon: CreditCard, label: 'Credit Report', desc: 'Credit balances and overdue tracking' },
      { path: '/sales-forecast', icon: TrendingUp, label: 'Sales Forecast', desc: 'AI-powered sales predictions' },
    ],
  },
  {
    title: 'Purchases & Expense Reports',
    color: 'from-red-500 to-rose-600',
    reports: [
      { path: '/purchases-summary', icon: Truck, label: 'Purchases Summary', desc: 'Unified bills + payments by branch, supplier, mode' },
      { path: '/cost-center-report', icon: Building2, label: 'Cost Center', desc: 'Expenses grouped by branch/cost center' },
      { path: '/supplier-report', icon: FileText, label: 'Supplier Report', desc: 'Supplier balance & payment breakdown' },
      { path: '/supplier-aging', icon: Clock, label: 'Supplier Aging', desc: 'Outstanding payment aging analysis' },
      { path: '/branch-cost-analyzer', icon: Activity, label: 'Branch Cost Analyzer', desc: 'Per-branch cost & profitability analysis' },
    ],
  },
  {
    title: 'Cash & Bank',
    color: 'from-blue-500 to-indigo-600',
    reports: [
      { path: '/cash-flow', icon: ArrowLeftRight, label: 'Cash Flow', desc: 'Cash inflows and outflows' },
      { path: '/bank-reconciliation', icon: Banknote, label: 'Bank Reconciliation', desc: 'Match bank statements to records' },
      { path: '/detailed-report', icon: Wallet, label: 'Detailed Report', desc: 'Cash, bank & credit breakdown' },
      { path: '/bank-statements', icon: FileText, label: 'Bank Statements', desc: 'Upload and process bank statements' },
      { path: '/bank-accounts', icon: Building2, label: 'Bank Accounts', desc: 'Manage company bank accounts' },
    ],
  },
  {
    title: 'Financial Overview',
    color: 'from-purple-500 to-fuchsia-600',
    reports: [
      { path: '/financial-dashboard', icon: PieChart, label: 'Financial Dashboard', desc: 'KPIs and financial overview' },
      { path: '/financial-ratios', icon: Activity, label: 'Financial Ratios', desc: 'Key financial health ratios' },
      { path: '/enhanced-pnl', icon: TrendingUp, label: 'Enhanced P&L', desc: 'Detailed profit & loss breakdown' },
      { path: '/budget-vs-actual', icon: DollarSign, label: 'Budget vs Actual', desc: 'Compare budgets against actuals' },
    ],
  },
  {
    title: 'HR & Workforce Reports',
    color: 'from-sky-500 to-blue-600',
    reports: [
      { path: '/hr-analytics', icon: BarChart2, label: 'HR Analytics', desc: 'Employee metrics and workforce insights' },
      { path: '/hr-documents', icon: FileText, label: 'Employee Documents', desc: 'Visa, Iqama, Passport, Work Permit expiry tracking + alerts' },
      { path: '/staff-performance', icon: Award, label: 'Staff Performance', desc: 'Employee performance tracking' },
      { path: '/shift-report', icon: CalendarDays, label: 'Shift Report', desc: 'Shift attendance and coverage' },
      { path: '/performance-report', icon: Activity, label: 'Performance Report', desc: 'Overall performance metrics' },
    ],
  },
  {
    title: 'Factory & Production Reports',
    color: 'from-stone-500 to-stone-700',
    reports: [
      { path: '/factory-analytics', icon: Factory, label: 'Production Analytics', desc: 'Manufacturing output and efficiency' },
      { path: '/quality-control', icon: TestTubes, label: 'Quality Control', desc: 'Quality inspection reports' },
    ],
  },
  {
    title: 'Advanced & AI',
    color: 'from-violet-500 to-purple-600',
    reports: [
      { path: '/advanced-analytics', icon: Zap, label: 'Advanced Analytics', desc: 'Deep data analysis and insights' },
      { path: '/anomaly-detection', icon: AlertTriangle, label: 'Anomaly Detection', desc: 'Detect unusual patterns' },
      { path: '/ai-hub', icon: Brain, label: 'AI Hub', desc: 'AI-powered business insights' },
      { path: '/report-builder', icon: FileText, label: 'Report Builder', desc: 'Custom report creation tool' },
      { path: '/export-center', icon: Download, label: 'Export Center', desc: 'Bulk data export' },
    ],
  },
  {
    title: 'Other',
    color: 'from-stone-400 to-stone-500',
    reports: [
      { path: '/recurring-entries', icon: Repeat, label: 'Recurring Entries', desc: 'Auto-posted journal entry templates' },
      { path: '/year-end-closing', icon: CalendarDays, label: 'Year-End Closing', desc: 'Close fiscal year accounts' },
      { path: '/partner-pl-report', icon: DollarSign, label: 'Partner P&L', desc: 'Profit & loss per partner' },
      { path: '/cross-branch-report', icon: Globe, label: 'Cross-Branch', desc: 'Multi-branch comparison report' },
    ],
  },
];

const FAV_KEY = 'accounting_reports_favorites_v1';

export default function AccountingReportsPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); } catch { return []; }
  });
  const [query, setQuery] = useState('');

  useEffect(() => {
    try { localStorage.setItem(FAV_KEY, JSON.stringify(favorites)); } catch { /* quota — ignore */ }
    // Notify the sidebar so it re-renders pinned items in-tab instantly.
    window.dispatchEvent(new CustomEvent('sidebar-favorites-updated'));
  }, [favorites]);

  const toggleFav = (path, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setFavorites(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };

  // Flatten every group into {...report, groupTitle, groupColor} so favorites
  // and search results can render with their original styling.
  const allReports = useMemo(() => {
    const flat = [];
    for (const g of reportGroups) {
      for (const r of g.reports) flat.push({ ...r, groupTitle: g.title, groupColor: g.color });
    }
    return flat;
  }, []);

  const favoriteReports = useMemo(
    () => favorites.map(p => allReports.find(r => r.path === p)).filter(Boolean),
    [favorites, allReports]
  );

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    return allReports.filter(r =>
      r.label.toLowerCase().includes(q) ||
      (r.desc || '').toLowerCase().includes(q) ||
      r.groupTitle.toLowerCase().includes(q)
    );
  }, [q, allReports]);

  const ReportCard = ({ report, compact = false }) => {
    const Icon = report.icon;
    const isFav = favorites.includes(report.path);
    const groupColor = report.groupColor || 'from-indigo-500 to-violet-600';
    return (
      <Card
        className="cursor-pointer hover:shadow-md hover:border-border transition-all duration-200 group relative"
        onClick={() => navigate(report.path)}
        data-testid={`report-card-${report.path.slice(1)}`}
      >
        <CardContent className={`${compact ? 'p-3' : 'p-4'} flex items-start gap-3`}>
          <div className={`${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-xl bg-gradient-to-br ${groupColor} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
            <Icon size={compact ? 14 : 16} className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground group-hover:text-foreground truncate">{report.label}</p>
            {!compact && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{report.desc}</p>}
          </div>
          <button
            onClick={(e) => toggleFav(report.path, e)}
            className={`absolute top-2 right-2 p-1 rounded-md transition-all ${isFav ? 'text-amber-500 hover:bg-amber-50 opacity-100' : 'text-muted-foreground/40 hover:text-amber-500 opacity-0 group-hover:opacity-100'}`}
            title={isFav ? 'Remove from favorites' : 'Add to favorites'}
            data-testid={`fav-toggle-${report.path.slice(1)}`}
          >
            <Star size={14} fill={isFav ? 'currentColor' : 'none'} strokeWidth={2} />
          </button>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-7" data-testid="accounting-reports-page">
        {/* iter405 — ZATCA CSID expiry banner. Renders nothing when
            CSID is healthy or Phase 2 is disabled. */}
        <ZatcaCsidBanner testidPrefix="reports-csid-banner" />
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">All Reports</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {reportGroups.reduce((s, g) => s + g.reports.length, 0)} reports across {reportGroups.length} categories
                {favorites.length > 0 && <span className="ml-2 text-amber-600">· {favorites.length} favorite{favorites.length === 1 ? '' : 's'}</span>}
              </p>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reports..."
              className="w-full pl-8 pr-8 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="reports-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground" data-testid="reports-search-clear">
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Search view */}
        {searchResults && (
          <div data-testid="reports-search-results">
            <div className="flex items-center gap-2 mb-3">
              <Search size={14} className="text-muted-foreground" />
              <h2 className="text-base font-semibold">Search results</h2>
              <span className="text-xs text-muted-foreground">{searchResults.length} {searchResults.length === 1 ? 'report' : 'reports'}</span>
            </div>
            {searchResults.length === 0 ? (
              <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No reports match "<span className="font-medium">{query}</span>"</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {searchResults.map(r => <ReportCard key={r.path} report={r} />)}
              </div>
            )}
          </div>
        )}

        {/* Favorites strip — hidden when searching */}
        {!searchResults && favoriteReports.length > 0 && (
          <div data-testid="favorites-strip">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
              <Star size={14} className="text-amber-500" fill="currentColor" />
              <h2 className="text-base font-semibold text-foreground">Favorites</h2>
              <span className="text-xs text-muted-foreground">{favoriteReports.length}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {favoriteReports.map(r => <ReportCard key={`fav-${r.path}`} report={r} compact />)}
            </div>
          </div>
        )}

        {/* Empty-favorites nudge — first time only */}
        {!searchResults && favoriteReports.length === 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="favorites-empty-nudge">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star size={16} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Pin your most-used reports</p>
                <p className="text-xs text-amber-800/80 mt-0.5">Hover any report card and click the star to add it to your Favorites strip for one-click access.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grouped full list — hidden when searching */}
        {!searchResults && reportGroups.map((group) => (
          <div key={group.title}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-1 h-5 rounded-full bg-gradient-to-b ${group.color}`} />
              <h2 className="text-base font-semibold text-foreground">{group.title}</h2>
              <span className="text-xs text-muted-foreground ml-1">{group.reports.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {group.reports.map(r => <ReportCard key={r.path} report={{ ...r, groupColor: group.color, groupTitle: group.title }} />)}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
